/**
 * Local project storage in IndexedDB (via `idb`, ISC license). No account, no server.
 * Records hold the versioned project (without the image) plus the image bytes as an ArrayBuffer.
 */
import { t } from '../i18n/i18n';
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import { newId } from '../engine/model/design';
import type { Design } from '../engine/model/types';
import {
  PROJECT_FORMAT,
  PROJECT_VERSION,
  type ProjectFile,
  type StoredImage,
  base64ToBytes,
  bytesToBase64,
  migrateProject,
  normalizeFolder,
} from './format';

export interface ImageData8 {
  bytes: ArrayBuffer;
  mime: string;
  name: string;
  width: number;
  height: number;
}

export interface ProjectRecord {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  /** Project file without the image (image: null). */
  file: ProjectFile;
  image: ImageData8 | null;
  /** Small PNG data URL for the project list. */
  thumbnail: string | null;
}

export interface ProjectSummary {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  thumbnail: string | null;
  objectCount: number;
  folder: string | null;
}

/** Autosaved working state (survives reloads); separate from the explicit project list. */
export interface SessionRecord {
  key: 'current';
  savedAt: string;
  /** Project file without image (validated/migrated on restore like any project). */
  file: ProjectFile;
  project: { id: string | null; name: string; createdAt: string | null; lastSaved: string | null; dirty: boolean; cloud?: boolean };
  view: { zoom: number; panX: number; panY: number } | null;
  /** Identifies the image stored under key 'image' (null = no image). */
  imageToken: string | null;
}

export interface SessionImage {
  key: 'image';
  token: string;
  image: ImageData8;
}

interface Schema extends DBSchema {
  projects: { key: string; value: ProjectRecord; indexes: { updatedAt: string } };
  session: { key: string; value: SessionRecord | SessionImage };
}

const DB_NAME = 'stitchcraft';
const DB_VERSION = 2;
let dbPromise: Promise<IDBPDatabase<Schema>> | null = null;

export function db(): Promise<IDBPDatabase<Schema>> {
  if (!dbPromise) {
    if (typeof indexedDB === 'undefined') return Promise.reject(new Error(t('This browser does not support local storage (IndexedDB).')));
    dbPromise = openDB<Schema>(DB_NAME, DB_VERSION, {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) {
          const store = database.createObjectStore('projects', { keyPath: 'id' });
          store.createIndex('updatedAt', 'updatedAt');
        }
        if (oldVersion < 2) database.createObjectStore('session', { keyPath: 'key' });
      },
    });
  }
  return dbPromise;
}

/** For tests: forget the cached connection. */
export function resetDbConnection(): void {
  dbPromise = null;
}

export interface LoadedProject {
  file: ProjectFile;
  image: ImageData8 | null;
}

export function buildProjectFile(id: string, name: string, design: Design, createdAt?: string, folder: string | null = null): ProjectFile {
  const now = new Date().toISOString();
  return {
    format: PROJECT_FORMAT,
    version: PROJECT_VERSION,
    id,
    name,
    createdAt: createdAt ?? now,
    updatedAt: now,
    app: { name: 'StitchCraft', version: '0.1.0' },
    design: { ...design, name },
    image: null,
    folder,
  };
}

export async function saveProject(file: ProjectFile, image: ImageData8 | null, thumbnail: string | null): Promise<ProjectRecord> {
  const rec: ProjectRecord = {
    id: file.id,
    name: file.name,
    createdAt: file.createdAt,
    updatedAt: file.updatedAt,
    file: { ...file, image: null },
    image,
    thumbnail,
  };
  await (await db()).put('projects', rec);
  return rec;
}

export async function listProjects(): Promise<ProjectSummary[]> {
  const all = await (await db()).getAllFromIndex('projects', 'updatedAt');
  return all
    .reverse()
    .map((r) => ({ id: r.id, name: r.name, createdAt: r.createdAt, updatedAt: r.updatedAt, thumbnail: r.thumbnail, objectCount: r.file?.design?.objects?.length ?? 0, folder: normalizeFolder(r.file?.folder) }));
}

export async function loadProject(id: string): Promise<LoadedProject> {
  const rec = await (await db()).get('projects', id);
  if (!rec) throw new Error(t('Project not found. It may have been deleted.'));
  return { file: migrateProject(rec.file), image: rec.image };
}

export async function getProjectRecord(id: string): Promise<ProjectRecord | undefined> {
  return (await db()).get('projects', id);
}

export async function deleteProject(id: string): Promise<void> {
  await (await db()).delete('projects', id);
}

export async function renameProject(id: string, name: string): Promise<void> {
  const d = await db();
  const rec = await d.get('projects', id);
  if (!rec) throw new Error(t('Project not found.'));
  const updatedAt = new Date().toISOString();
  await d.put('projects', { ...rec, name, updatedAt, file: { ...rec.file, name, updatedAt, design: { ...rec.file.design, name } } });
}

/** Moves a project to a folder (null = no folder). The last-changed time is kept. */
export async function moveProject(id: string, folder: string | null): Promise<void> {
  const d = await db();
  const rec = await d.get('projects', id);
  if (!rec) throw new Error(t('Project not found.'));
  await d.put('projects', { ...rec, file: { ...rec.file, folder: normalizeFolder(folder) } });
}

export async function duplicateProject(id: string, name?: string): Promise<string> {
  const d = await db();
  const rec = await d.get('projects', id);
  if (!rec) throw new Error(t('Project not found.'));
  const newIdValue = newId('p');
  const now = new Date().toISOString();
  const copyName = name ?? t('{name} (copy)', { name: rec.name });
  await d.put('projects', {
    ...rec,
    id: newIdValue,
    name: copyName,
    createdAt: now,
    updatedAt: now,
    file: { ...rec.file, id: newIdValue, name: copyName, createdAt: now, updatedAt: now, design: { ...rec.file.design, name: copyName } },
  });
  return newIdValue;
}

/** Converts a loaded project into a portable file with the embedded image. */
export function toPortableFile(p: LoadedProject): ProjectFile {
  let image: StoredImage | null = null;
  if (p.image) {
    image = { mime: p.image.mime, base64: bytesToBase64(new Uint8Array(p.image.bytes)), name: p.image.name, width: p.image.width, height: p.image.height };
  }
  return { ...p.file, image };
}

/** Converts an imported portable file into storage form (new id to avoid overwriting). */
export function fromPortableFile(file: ProjectFile, keepId = false): LoadedProject {
  const image: ImageData8 | null = file.image
    ? (() => {
        const bytes = base64ToBytes(file.image.base64);
        return { bytes: bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer, mime: file.image.mime, name: file.image.name, width: file.image.width, height: file.image.height };
      })()
    : null;
  const id = keepId ? file.id : newId('p');
  return { file: { ...file, id, image: null }, image };
}

// ---------------------------------------------------------------- autosaved session

export async function saveSession(rec: Omit<SessionRecord, 'key' | 'savedAt'>, image: { token: string; image: ImageData8 } | null): Promise<void> {
  const d = await db();
  const tx = d.transaction('session', 'readwrite');
  await tx.store.put({ ...rec, key: 'current', savedAt: new Date().toISOString(), file: { ...rec.file, image: null } });
  if (image) await tx.store.put({ key: 'image', token: image.token, image: image.image });
  else if (rec.imageToken === null) await tx.store.delete('image');
  await tx.done;
}

export interface LoadedSession {
  record: SessionRecord;
  file: ProjectFile;
  image: ImageData8 | null;
}

/** Loads the autosaved session; invalid or unreadable sessions are discarded (returns null). */
export async function loadSession(): Promise<LoadedSession | null> {
  const d = await db();
  const rec = (await d.get('session', 'current')) as SessionRecord | undefined;
  if (!rec) return null;
  try {
    const file = migrateProject(rec.file);
    let image: ImageData8 | null = null;
    if (rec.imageToken) {
      const img = (await d.get('session', 'image')) as SessionImage | undefined;
      if (img && img.token === rec.imageToken) image = img.image;
    }
    return { record: rec, file, image };
  } catch {
    await clearSession();
    return null;
  }
}

export async function clearSession(): Promise<void> {
  const d = await db();
  await d.clear('session');
}
